import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { useCallback, Fragment as Fragment$1 } from "react";
import { useLocation } from "react-router-dom";
import { useColors, useTheme } from "@inbody/core";
import { useAuth } from "@inbody/keycloak";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ListItemIcon, Stack, Box } from "@mui/material";
import Collapse from "@mui/material/Collapse";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import Typography from "@mui/material/Typography";
import { isMenuItemVisible } from "../../side-menu/utils/isMenuItemVisible.js";
import { MenuItemButton } from "./MenuItemButton.js";
import { MenuSectionHeader } from "./MenuSectionHeader.js";
import { useMenuStore } from "../../side-menu/store/SideMenuStore.js";
function ExpandMenuItem(props) {
  const {
    children,
    iconNode,
    id,
    label,
    collapsed = false,
    transitioning = false,
    compact = false
  } = props;
  const { pathname } = useLocation();
  const expandedMenus = useMenuStore((state) => state.expandedMenus);
  const toggleExpandedMenu = useMenuStore((state) => state.toggleExpandedMenu);
  const { checkRoles } = useAuth();
  const { gray, brand } = useColors();
  const { isDarkMode } = useTheme();
  const isMenuExpand = expandedMenus.includes(id);
  const handleToggleMenuExpand = useCallback(() => {
    toggleExpandedMenu(id);
  }, [id, toggleExpandedMenu]);
  const visibleChildren = children.filter(
    (subMenuItem) => subMenuItem.type === "link" && isMenuItemVisible(subMenuItem, checkRoles)
  );
  const visibleNestedChildren = children.filter(
    (subMenuItem) => subMenuItem.type === "expand" && subMenuItem.children.some(
      (grandChild) => grandChild.type === "link" && isMenuItemVisible(grandChild, checkRoles)
    )
  );
  if (visibleChildren.length === 0 && visibleNestedChildren.length === 0) {
    return null;
  }
  const sections = visibleChildren.reduce((acc, subMenuItem) => {
    const lastSection = acc[acc.length - 1];
    if (lastSection && lastSection.sectionLabel === subMenuItem.sectionLabel) {
      lastSection.items.push(subMenuItem);
      return acc;
    }
    acc.push({ sectionLabel: subMenuItem.sectionLabel, items: [subMenuItem] });
    return acc;
  }, []);
  const sectionTrayStyles = {
    marginTop: "8px",
    paddingBottom: "6px",
    borderTop: `1px solid ${gray[100]}`,
    backgroundColor: isDarkMode ? gray[10] : gray[50]
  };
  const parentButtonStyles = {
    bgcolor: "background.paper",
    padding: compact ? "10px 20px" : "12px 20px",
    margin: "2px 0",
    borderLeft: "3px solid transparent",
    color: gray[800],
    "&:hover": {
      bgcolor: gray[50],
      color: brand[500]
    },
    ...isMenuExpand && {
      bgcolor: brand[25],
      color: brand[500],
      borderLeftColor: brand[500]
    },
    ...collapsed && { justifyContent: "center", px: 0 }
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(ListItem, { disablePadding: true, children: /* @__PURE__ */ jsxs(
      ListItemButton,
      {
        onClick: handleToggleMenuExpand,
        sx: parentButtonStyles,
        children: [
          iconNode && /* @__PURE__ */ jsx(
            ListItemIcon,
            {
              sx: {
                minWidth: collapsed ? 0 : 28,
                width: collapsed ? "auto" : 28,
                marginRight: collapsed ? 0 : 1.5,
                justifyContent: "center",
                color: "inherit",
                "& svg": {
                  fontSize: "20px"
                }
              },
              children: iconNode
            }
          ),
          !collapsed && /* @__PURE__ */ jsxs(
            Stack,
            {
              direction: "row",
              justifyContent: "space-between",
              alignItems: "center",
              sx: { flex: 1, minWidth: 0, overflow: "hidden" },
              children: [
                /* @__PURE__ */ jsx(
                  Typography,
                  {
                    variant: compact ? "caption" : "body2",
                    sx: {
                      fontWeight: 500,
                      color: "inherit",
                      ...transitioning && {
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }
                    },
                    children: label
                  }
                ),
                /* @__PURE__ */ jsx(
                  Box,
                  {
                    sx: {
                      fontSize: "10px",
                      color: isMenuExpand ? brand[500] : gray[700],
                      transition: "transform 0.2s ease",
                      transform: isMenuExpand ? "rotate(180deg)" : "rotate(0deg)"
                    },
                    children: /* @__PURE__ */ jsx(ExpandMoreIcon, { fontSize: "small" })
                  }
                )
              ]
            }
          )
        ]
      }
    ) }, id),
    !collapsed && /* @__PURE__ */ jsxs(
      Collapse,
      {
        in: isMenuExpand,
        timeout: "auto",
        unmountOnExit: true,
        sx: {
          backgroundColor: gray[25]
        },
        children: [
          sections.map((section) => {
            const hoverBgColor = section.sectionLabel ? gray[100] : gray[50];
            const sectionItems = section.items.map((subMenuItem) => {
              const isSelected = pathname === subMenuItem.url;
              const subButtonStyles = {
                bgcolor: isSelected ? brand[25] : "transparent",
                color: isSelected ? brand[500] : gray[800],
                padding: "11px 20px 11px 60px",
                fontSize: "13px",
                position: "relative",
                "&:hover": {
                  bgcolor: hoverBgColor,
                  color: brand[500]
                },
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: "38px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: "5px",
                  height: "5px",
                  borderRadius: "50%",
                  backgroundColor: isSelected ? brand[500] : gray[300]
                },
                "&:hover::before": {
                  backgroundColor: brand[500]
                }
              };
              return /* @__PURE__ */ jsx(ListItem, { disablePadding: true, children: /* @__PURE__ */ jsx(
                MenuItemButton,
                {
                  url: subMenuItem.url,
                  target: subMenuItem.target,
                  label: subMenuItem.label,
                  variant: "caption",
                  sx: subButtonStyles
                }
              ) }, subMenuItem.id);
            });
            if (!section.sectionLabel) {
              return /* @__PURE__ */ jsx(Fragment$1, { children: sectionItems }, section.items[0].id);
            }
            return /* @__PURE__ */ jsxs(Box, { sx: sectionTrayStyles, children: [
              /* @__PURE__ */ jsx(
                MenuSectionHeader,
                {
                  label: section.sectionLabel,
                  iconNode: section.items[0].sectionIconNode
                }
              ),
              sectionItems
            ] }, section.items[0].id);
          }),
          visibleNestedChildren.map((nestedItem) => /* @__PURE__ */ jsx(NestedExpandItem, { ...nestedItem }, nestedItem.id))
        ]
      }
    )
  ] });
}
function NestedExpandItem(props) {
  const { id, label, children } = props;
  const { pathname } = useLocation();
  const expandedMenus = useMenuStore((state) => state.expandedMenus);
  const toggleExpandedMenu = useMenuStore((state) => state.toggleExpandedMenu);
  const { checkRoles } = useAuth();
  const { gray, brand } = useColors();
  const isMenuExpand = expandedMenus.includes(id);
  const handleToggleMenuExpand = useCallback(() => {
    toggleExpandedMenu(id);
  }, [id, toggleExpandedMenu]);
  const visibleChildren = children.filter(
    (subMenuItem) => subMenuItem.type === "link" && isMenuItemVisible(subMenuItem, checkRoles)
  );
  if (visibleChildren.length === 0) {
    return null;
  }
  const headerStyles = {
    color: isMenuExpand ? brand[500] : gray[800],
    padding: "11px 20px 11px 60px",
    position: "relative",
    "&:hover": {
      bgcolor: gray[50],
      color: brand[500]
    },
    "&::before": {
      content: '""',
      position: "absolute",
      left: "38px",
      top: "50%",
      transform: "translateY(-50%)",
      width: "5px",
      height: "5px",
      borderRadius: "50%",
      backgroundColor: isMenuExpand ? brand[500] : gray[300]
    }
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(ListItem, { disablePadding: true, children: /* @__PURE__ */ jsx(ListItemButton, { onClick: handleToggleMenuExpand, sx: headerStyles, children: /* @__PURE__ */ jsxs(
      Stack,
      {
        direction: "row",
        justifyContent: "space-between",
        alignItems: "center",
        sx: { flex: 1, minWidth: 0, overflow: "hidden" },
        children: [
          /* @__PURE__ */ jsx(
            Typography,
            {
              variant: "caption",
              sx: { fontWeight: 500, color: "inherit" },
              children: label
            }
          ),
          /* @__PURE__ */ jsx(
            Box,
            {
              sx: {
                color: isMenuExpand ? brand[500] : gray[700],
                transition: "transform 0.2s ease",
                transform: isMenuExpand ? "rotate(180deg)" : "rotate(0deg)",
                display: "flex"
              },
              children: /* @__PURE__ */ jsx(ExpandMoreIcon, { fontSize: "small" })
            }
          )
        ]
      }
    ) }) }),
    /* @__PURE__ */ jsx(Collapse, { in: isMenuExpand, timeout: "auto", unmountOnExit: true, children: visibleChildren.map((subMenuItem) => {
      const isSelected = pathname === subMenuItem.url;
      const subButtonStyles = {
        bgcolor: isSelected ? brand[25] : "transparent",
        color: isSelected ? brand[500] : gray[800],
        padding: "11px 20px 11px 80px",
        fontSize: "13px",
        position: "relative",
        "&:hover": {
          bgcolor: gray[50],
          color: brand[500]
        },
        "&::before": {
          content: '""',
          position: "absolute",
          left: "58px",
          top: "50%",
          transform: "translateY(-50%)",
          width: "5px",
          height: "5px",
          borderRadius: "50%",
          backgroundColor: isSelected ? brand[500] : gray[300]
        },
        "&:hover::before": {
          backgroundColor: brand[500]
        }
      };
      return /* @__PURE__ */ jsx(ListItem, { disablePadding: true, children: /* @__PURE__ */ jsx(
        MenuItemButton,
        {
          url: subMenuItem.url,
          target: subMenuItem.target,
          label: subMenuItem.label,
          variant: "caption",
          sx: subButtonStyles
        }
      ) }, subMenuItem.id);
    }) })
  ] });
}
export {
  ExpandMenuItem
};
